import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/layout/PageHeader';
import { ColorSettings } from '@/components/settings/whitelabel/ColorSettings';
import { ImageSettings } from '@/components/settings/whitelabel/ImageSettings';
import { TypographySettings } from '@/components/settings/whitelabel/TypographySettings';
import { BrandingPreview } from '@/components/settings/whitelabel/BrandingPreview';
import {
  pickOwnColors,
  pickInheritedColors,
  buildColorsPayload,
  buildFontPayload,
} from '@/components/settings/whitelabel/brandingDraft';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { LockCircle } from '@/components/gating/LockCircle';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { Tooltip } from '@/components/ui/Tooltip';
import { ICONS } from '@/constants/icons';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { cn } from '@/utils/cn';
import {
  getTeamBranding,
  updateTeamBranding,
  resetBranding,
} from '@/services/branding.service';
import {
  BrandingInput,
  BrandingColors,
  BrandingAssets,
  BrandingFont,
  TenantType,
} from '@/types/branding.types';
import { toast } from '@/utils/toast';
import { devLog } from '@/utils/logger';

/**
 * WhitelabelSettings Page
 *
 * Comprehensive whitelabel customization interface for agencies and workspaces
 *
 * Features:
 * - Color customization (11 colors)
 * - Image/asset management (19 images)
 * - Typography selection (Google Fonts)
 * - Live preview
 * - Save/reset functionality
 */

type SettingsTab = 'colors' | 'images' | 'typography' | 'preview';

export const WhitelabelSettings = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { user } = useAuth();
  const { refreshBranding } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<SettingsTab>('colors');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  useUnsavedGuard(hasUnsavedChanges);

  // Local state for draft changes
  const [draftColors, setDraftColors] = useState<BrandingColors>({});
  const [draftAssets, setDraftAssets] = useState<BrandingAssets>({});
  const [draftFont, setDraftFont] = useState<BrandingFont | undefined>();
  const [draftHeadlineFont, setDraftHeadlineFont] = useState<BrandingFont | undefined>();

  // Get current (effective) team ID: the brand open in God Mode, else the home team
  const teamId = user?.currentTeamId ?? user?.teamId;

  // Is the effective brand part of a Brand Portfolio? Staff in God Mode are always
  // inside one of their portfolio's brands; brand members belong to one via the home team.
  const isStaff = !!user?.isAgencyStaff;
  // Staff are in a portfolio via God Mode OR because their home team belongs to one.
  const inPortfolio = !!user?.godModeSession || user?.team?.agencyId != null;
  const canOpenPortfolioAtelier =
    isStaff &&
    (user?.agencyRole === 'OWNER' || user?.agencyRole === 'ADMIN' || user?.agencyRole === 'MANAGER');

  // Portfolio Atelier status for EVERYONE (staff in God Mode too): the branding API's
  // isWhitelabelEnabled is the backend SSOT (team package/add-on, agency add-on, plan
  // + inheritance), so the editor is never shown when saves would 403.
  const {
    data: portfolioBranding,
    isLoading: portfolioLoading,
    isError: portfolioError,
    refetch: refetchPortfolio,
  } = useQuery({
    queryKey: ['branding', 'team', teamId],
    queryFn: () => getTeamBranding(teamId!),
    // Standalone teams use the same backend rule: a package-entitled team gets the editor, not only add-on buyers
    enabled: !!teamId,
  });
  const portfolioHasAtelier = portfolioBranding?.isWhitelabelEnabled === true;

  // Standalone teams: own add-on. Brands in a Brand Portfolio: the editor is available
  // when the portfolio has Atelier (brands keep their OWN branding; empty fields fall
  // back to the portfolio's).
  const hasWhitelabelAccess = inPortfolio
    ? portfolioHasAtelier
    : portfolioHasAtelier || (user?.team?.hasWhitelabelAddon ?? false);

  // Check if team ID exists - can't proceed without it
  if (!teamId && hasWhitelabelAccess) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-md px-6">
          <Icon name={ICONS.ERROR} size="xl" color="text-red-500" className="mx-auto mb-4" ariaHidden />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('settings_whitelabel_no_team_title')}</h2>
          <p className="text-gray-600 mb-4">
            {t('settings_whitelabel_no_team_desc')}
          </p>
          <Button onClick={() => navigate('/dashboard')}>{t('settings_whitelabel_no_team_button')}</Button>
        </div>
      </div>
    );
  }

  // Fetch current branding
  const {
    data: branding,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['branding', 'team', teamId],
    queryFn: () => getTeamBranding(teamId!),
    enabled: hasWhitelabelAccess && !!teamId,
  });

  // Initialize draft state when branding first loads.
  // Guarded with a ref so background refetches (e.g. window-focus refetch,
  // or invalidation from handleAssetsChange) never re-seed the draft and
  // clobber in-progress unsaved edits. Post-save/reset state is refreshed
  // explicitly by the mutations' onSuccess handlers below.
  const seededRef = useRef(false);
  useEffect(() => {
    if (branding && !seededRef.current) {
      setDraftColors(pickOwnColors(branding, 'team'));
      setDraftAssets(branding.assets || {});
      setDraftFont(branding.font);
      setDraftHeadlineFont(branding.headlineFont);
      seededRef.current = true;
    }
  }, [branding]);

  // Save branding mutation
  const saveMutation = useMutation({
    mutationFn: (input: BrandingInput) => updateTeamBranding(teamId!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['branding', 'team', teamId] });
      setHasUnsavedChanges(false);
      toast.success(t('settings_whitelabel_success_save'));
      // Allow the ensuing refetch to re-seed the draft from the authoritative
      // saved branding, so fields stripped from the payload (e.g. emptied
      // hex colors) reflect the merged server value instead of the stale
      // local draft. Safe here: hasUnsavedChanges is false, so there is no
      // in-progress edit to clobber.
      seededRef.current = false;
      // Refresh live theme so changes are visible immediately without page reload
      refreshBranding();
    },
    onError: (error) => {
      devLog.error('Save error:', error);
      toast.error(t('settings_whitelabel_error_save'));
    },
  });

  // Reset branding mutation
  const resetMutation = useMutation({
    mutationFn: () => resetBranding(TenantType.TEAM, teamId!),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['branding', 'team', teamId] });
      setDraftColors(pickOwnColors(data, 'team'));
      setDraftAssets(data.assets || {});
      setDraftFont(data.font);
      setDraftHeadlineFont(data.headlineFont);
      setHasUnsavedChanges(false);
      setResetConfirmOpen(false);
      toast.success(t('settings_whitelabel_success_reset'));
      refreshBranding();
    },
    onError: (error) => {
      devLog.error('Reset error:', error);
      toast.error(t('settings_whitelabel_error_reset'));
    },
  });

  // Handle changes
  const handleColorsChange = (colors: BrandingColors) => {
    setDraftColors(colors);
    setHasUnsavedChanges(true);
  };

  const handleAssetsChange = (assets: BrandingAssets) => {
    setDraftAssets(assets);
    // Don't set hasUnsavedChanges — images are uploaded/deleted directly to S3 and DB
    // Refresh theme + re-fetch branding query so assetSources updates (badge accuracy)
    refreshBranding();
    queryClient.invalidateQueries({ queryKey: ['branding', 'team', teamId] });
  };

  const handleFontChange = (font: BrandingFont | undefined) => {
    setDraftFont(font);
    setHasUnsavedChanges(true);
  };

  const handleHeadlineFontChange = (font: BrandingFont | undefined) => {
    setDraftHeadlineFont(font);
    setHasUnsavedChanges(true);
  };

  // Save handler
  const handleSave = () => {
    // Empty color fields: a saved own value (source 'team') is cleared with null so the cascade
    // falls back to the Brand Portfolio / default; empty unsaved fields are omitted.
    const cleanedColors = buildColorsPayload(draftColors, branding, 'team');

    // Don't include assets — images are managed independently via upload/delete endpoints
    // Including them here would persist ALL resolved assets (including defaults) as team overrides
    const input: BrandingInput = {
      colors: cleanedColors,
      font: buildFontPayload(draftFont, branding?.font, branding?.sources?.font, 'team'),
      headlineFont: buildFontPayload(
        draftHeadlineFont,
        branding?.headlineFont,
        branding?.sources?.headlineFont,
        'team'
      ),
    };

    saveMutation.mutate(input);
  };

  // Reset handler
  const handleReset = () => {
    setResetConfirmOpen(true);
  };

  // Brand inside a Brand Portfolio WITHOUT Atelier (or status unknown): portfolio-scoped notice
  if (inPortfolio && !hasWhitelabelAccess) {
    if (portfolioLoading) {
      return (
        <div className="flex items-center justify-center min-h-[60vh]">
          <Icon name={ICONS.LOADER} size="xl" className="animate-spin text-secondary-500" ariaHidden />
        </div>
      );
    }
    const portfolioStatusUnknown = portfolioError;
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center max-w-xl px-6">
          {portfolioHasAtelier ? (
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-secondary-100 to-secondary-200 flex items-center justify-center">
              <Icon name={ICONS.PAINT} size="xl" color="text-secondary-600" ariaHidden />
            </div>
          ) : (
            <LockCircle size={80} glyphSize={36} className="mx-auto mb-6 bg-gradient-to-br from-secondary-100 to-secondary-200 text-secondary-600" />
          )}
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            {portfolioHasAtelier || portfolioStatusUnknown
              ? t('settings_whitelabel_portfolio_managed')
              : t('settings_whitelabel_portfolio_addon')}
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <Button variant="secondary" size="lg" onClick={() => navigate('/settings?tab=account')}>
              <Icon name={ICONS.ARROW_LEFT} size="xs" className="mr-2" ariaHidden />
              {t('settings_whitelabel_back_button')}
            </Button>
            {portfolioStatusUnknown && (
              <Button variant="primary" size="lg" onClick={() => void refetchPortfolio()}>
                {t('portfolio_load_error_retry')}
              </Button>
            )}
            {!portfolioHasAtelier && !portfolioStatusUnknown && canOpenPortfolioAtelier && (
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate('/shop?details=atelier-mode#shop-category-add-ons')}
              >
                <Icon name={ICONS.CROWN} size="xs" className="mr-2" ariaHidden />
                {t('settings_whitelabel_shop_button')}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Standalone team: the branding status is still loading, so access is undecided (no upgrade-wall flash)
  if (!inPortfolio && !hasWhitelabelAccess && portfolioLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Icon name={ICONS.LOADER} size="xl" className="animate-spin text-secondary-500" ariaHidden />
      </div>
    );
  }

  // Permission check - show upgrade prompt if no access
  if (!hasWhitelabelAccess) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-2xl px-6">
            {/* Icon */}
            <LockCircle size={80} glyphSize={36} className="mx-auto mb-6 bg-gradient-to-br from-secondary-100 to-secondary-200 text-secondary-600" />

            {/* Heading */}
            <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('settings_whitelabel_upgrade_title')}</h2>
            <p className="text-lg text-gray-600 mb-8">
              {t('settings_whitelabel_upgrade_subtitle')}
            </p>

            {/* Features List */}
            <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
                {t('settings_whitelabel_features_label')}
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Icon name={ICONS.CHECK} size="sm" color="text-green-600" className="mt-0.5 flex-shrink-0" ariaHidden />
                  <span className="text-sm text-gray-700">
                    {t('settings_whitelabel_feature_colors')}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name={ICONS.CHECK} size="sm" color="text-green-600" className="mt-0.5 flex-shrink-0" ariaHidden />
                  <span className="text-sm text-gray-700">
                    {t('settings_whitelabel_feature_images')}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name={ICONS.CHECK} size="sm" color="text-green-600" className="mt-0.5 flex-shrink-0" ariaHidden />
                  <span className="text-sm text-gray-700">
                    {t('settings_whitelabel_feature_fonts')}
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Icon name={ICONS.CHECK} size="sm" color="text-green-600" className="mt-0.5 flex-shrink-0" ariaHidden />
                  <span className="text-sm text-gray-700">
                    {t('settings_whitelabel_feature_client')}
                  </span>
                </li>
              </ul>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button
                variant="secondary"
                size="lg"
                onClick={() => navigate('/settings?tab=account')}
              >
                <Icon name={ICONS.ARROW_LEFT} size="xs" className="mr-2" ariaHidden />
                {t('settings_whitelabel_back_button')}
              </Button>
              <Button
                variant="primary"
                size="lg"
                onClick={() => navigate('/shop?details=atelier-mode#shop-category-add-ons')}
              >
                <Icon name={ICONS.CROWN} size="xs" className="mr-2" ariaHidden />
                {t('settings_whitelabel_shop_button')}
              </Button>
            </div>

            {/* Current Plan Info */}
            {user?.package && (
              <p className="text-xs text-gray-500 mt-6">
                {t('settings_whitelabel_current_plan')} <span className="font-semibold capitalize">{user.package.name}</span>
              </p>
            )}
          </div>
        </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Icon name={ICONS.LOADER} size="xl" className="animate-spin mx-auto mb-4 text-secondary-500" ariaHidden />
            <p className="text-gray-600">{t('settings_whitelabel_loading')}</p>
          </div>
        </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <Icon name={ICONS.ERROR} size="xl" color="text-red-500" className="mx-auto mb-4" ariaHidden />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('settings_whitelabel_error_title')}</h2>
            <p className="text-gray-600 mb-4">
              {t('settings_whitelabel_error_desc')}
            </p>
            <Button onClick={() => window.location.reload()}>{t('settings_whitelabel_reload_button')}</Button>
          </div>
        </div>
    );
  }

  const tabs: { id: SettingsTab; label: string; icon: string }[] = [
    { id: 'colors', label: t('settings_whitelabel_tab_colors'), icon: ICONS.COLOR },
    { id: 'images', label: t('settings_whitelabel_tab_images'), icon: ICONS.IMAGE },
    { id: 'typography', label: t('settings_whitelabel_tab_typography'), icon: ICONS.PEN },
    { id: 'preview', label: t('settings_whitelabel_tab_preview'), icon: ICONS.EYE },
  ];

  return (
    <div className="space-y-6">
        {/* Page Header */}
        <PageHeader
          title={t('settings_whitelabel_page_title')}
          subtitle={t('settings_whitelabel_page_subtitle')}
          icon={ICONS.PAINT}
          headerType="dashboard"
          documentTitle="Whitelabel Settings"
          backTo="/settings"
          backLabel={t('nav_settings', { defaultValue: 'Settings' })}
        />

        {inPortfolio && (
          <p className="flex flex-wrap items-center gap-x-2 text-sm text-gray-600">
            <Icon name={ICONS.INFO} size="sm" color="text-gray-400" ariaHidden />
            <span>{t('settings_whitelabel_brand_inherits_hint')}</span>
            {canOpenPortfolioAtelier && (
              <Link
                to="/agency/settings/whitelabel"
                className="font-medium text-secondary-600 hover:underline"
              >
                {t('settings_whitelabel_brand_portfolio_link')}
              </Link>
            )}
          </p>
        )}

        {/* Tabs + Action Buttons Row */}
        <div className="sticky top-[4.1rem] z-30 bg-gray-50/95 backdrop-blur-sm py-3 sm:py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4">
          {/* Pill-Style Tabs — mobile: 4-col compact grid, desktop: inline row */}
          <div className="bg-gray-100/80 backdrop-blur-sm p-2 rounded-2xl">
            {/* Mobile grid */}
            <div className="grid grid-cols-4 gap-1 sm:hidden">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex flex-col items-center justify-center gap-1 px-2 py-2.5 rounded-xl transition-all duration-200',
                    activeTab === tab.id
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-500 hover:text-gray-900 hover:bg-white/50'
                  )}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                >
                  <Icon
                    name={tab.icon}
                    size="sm"
                    color={activeTab === tab.id ? 'text-primary-600' : 'text-gray-400'}
                    ariaHidden
                  />
                  <span className="text-[10px] font-medium leading-tight truncate max-w-full">{tab.label}</span>
                </button>
              ))}
            </div>
            {/* Desktop inline row */}
            <div className="hidden sm:inline-flex gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'group inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 whitespace-nowrap',
                    activeTab === tab.id
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  )}
                  aria-current={activeTab === tab.id ? 'page' : undefined}
                >
                  <Icon
                    name={tab.icon}
                    size="sm"
                    color={activeTab === tab.id ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600'}
                    ariaHidden
                  />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Design reference (always visible) + Save/Reset (only when changes exist) */}
          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            <Link
              to="/design-system"
              className="hidden sm:inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              <Icon name={ICONS.EXTERNAL_LINK} size="xs" ariaHidden />
              {t('settings_atelier_design_reference', { defaultValue: 'Design reference' })}
            </Link>

            {hasUnsavedChanges && (
              <div className="flex items-center gap-2 animate-fade-in w-full sm:w-auto">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleReset}
                  disabled={resetMutation.isPending}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5"
                >
                  <Icon name={ICONS.REFRESH} size="sm" ariaHidden />
                  {t('settings_whitelabel_reset_button')}
                </Button>

                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || saveMutation.isPending}
                  loading={saveMutation.isPending}
                  className="flex-1 sm:flex-none flex items-center justify-center gap-1.5"
                >
                  <Icon name={ICONS.SAVE} size="sm" ariaHidden />
                  {t('settings_whitelabel_save_button')}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-mint" />
          <div className="p-4 sm:p-6">
          {activeTab === 'colors' && (
            <ColorSettings
              colors={draftColors}
              onChange={handleColorsChange}
              inheritedColors={branding ? pickInheritedColors(branding, 'team') : undefined}
              scope={inPortfolio ? 'portfolio-brand' : 'standalone'}
            />
          )}

          {activeTab === 'images' && (
            <ImageSettings
              tenantType={TenantType.TEAM}
              tenantId={teamId!}
              assets={draftAssets}
              assetSources={branding?.sources?.assets}
              onChange={handleAssetsChange}
            />
          )}

          {activeTab === 'typography' && (
            <TypographySettings
              font={draftFont}
              headlineFont={draftHeadlineFont}
              onChange={handleFontChange}
              onHeadlineFontChange={handleHeadlineFontChange}
              inheritedFont={branding?.sources?.font !== 'team' ? branding?.font : undefined}
              inheritedHeadlineFont={
                branding?.sources?.headlineFont !== 'team' ? branding?.headlineFont : undefined
              }
              scope={inPortfolio ? 'portfolio-brand' : 'standalone'}
            />
          )}

          {activeTab === 'preview' && (
            <BrandingPreview
              branding={{
                colors: {
                  ...(branding?.colors ?? {}),
                  ...Object.fromEntries(
                    Object.entries(draftColors).filter(([, v]) => typeof v === 'string' && v.trim())
                  ),
                },
                assets: draftAssets,
                font: draftFont,
                headlineFont: draftHeadlineFont,
              }}
            />
          )}
          </div> {/* p-6 */}
        </div> {/* card */}


        {/* Reset Confirmation Modal */}
        <ConfirmationModal
          open={resetConfirmOpen}
          onOpenChange={setResetConfirmOpen}
          title={t('settings_whitelabel_confirm_reset_title', { defaultValue: 'Reset Branding' })}
          description={t('settings_whitelabel_confirm_reset')}
          confirmText={t('settings_whitelabel_reset_button')}
          variant="warning"
          onConfirm={() => resetMutation.mutate()}
          isLoading={resetMutation.isPending}
        />
      </div>
  );
};
